import type { ErrorObject } from 'ajv';
import type { google as googleMaps } from 'google-maps';
import { User } from '@angular/fire/auth';

// eslint-disable-next-line no-var
declare var google: googleMaps;

export enum TypesOfShippings {
  TAKE_AWAY = 'TAKE_AWAY',
  DELIVERY = 'DELIVERY',
  IN_PLACE = 'IN_PLACE',
  PARCEL = 'PARCEL',
}

export type AppUserCustomClaims = {
  appSuperAdmin?: boolean;
  appInvitedUser?: boolean;
};

export type UserDataWithClaims = Pick<User, 'uid' | 'displayName' | 'photoURL' | 'email'> & {
  claims: AppUserCustomClaims;
};

export type AuthProvider = 'password' | 'google.com' | 'facebook.com'; // phone, twitter.com, github.com, apple.com, yahoo.com, hotmail.com
export type InvitedUser = {
  id?: string;
  establishmentId: string;
  status: 'pending' | 'accepted';
  uid?: string;
  details: {
    name: string;
    email: string;
    phone?: string;
    jobTitle?: string;
    roles: InvitedUserRole[];
    invitationDate: Date;
    invitedBy: string;
    authProvider?: AuthProvider;
    created: Date;
    updated: Date;
  };
};

export const invitedUserRoles = [
  'ESTABLISHMENT_ADMIN',
  'GENERAL_INFO_ADMIN',
  'CATALOG_ADMIN',
  'PAYMENT_ADMIN',
  'SHIPPING_ADMIN',
  'CUSTOM_FIELDS_ADMIN',
  'COUPONS_ADMIN',
  'ORDERS_ADMIN',
  'ORDERS_EDITOR_ADMIN',
  'BILLING_ADMIN',
  'STATISTICS_ADMIN',
  'INTEGRATIONS_ADMIN',
  'ANALYTICS_ADMIN',
  'ADS_ADMIN',
] as const;

export type InvitedUserRole = (typeof invitedUserRoles)[number];

export interface Establishment {
  id: string;
  created?: Date; // NOTE: this was added on 2024 and no backfill was used for old data
  updated?: Date; // NOTE: this was added on 2024 and no backfill was used for old data
  logo: string;
  name: string;
  slug: string;
  userId: string;
  userIds: Record<string, InvitedUserRole[]>;
  phone: string;
  additionalPhone?: string;
  image?: string; // for subsidiaries
  theme?: EstablishmentTheme;
  subsidiaries: string[];
  cloneTargets?: string[];
  clonedFrom?: string;
  // @deprecated
  address: string;
  addressDetails?: AddressDetails;
  addressReferences?: string;
  currencyCode: CurrencyCode;
  altLanguage: ALT_LANGUAGES;
  categories: string[];
  isDigitalMenu: boolean;
  countryCode?: Country;
  countryDetail?: string;
  contactInfo: ContactInfo;
  subscription?: SubscriptionInfo;
  workingDays?: WorkingDays;
  temporaryClosure?: TemporaryClosure;
  facebookPixel?: string;
  googleAnalytics?: string;
  googleTagManager?: string;
  facebookDomainVerification?: string;
  seoSocialMedia?: EstablishmentSeoSocialMedia;
  features: Feature[];
  links?: {
    website: string;
    instagram: string;
    facebook: string;
    twitter: string;
    tiktok: string;
  };
  productsExport?: EstablishmentProductsExport;
  subdomainStatus?: 'PENDING' | 'ACTIVE' | 'INACTIVE';
  subdomainRedirection?: boolean;
  customDomain?: CustomDomain;
  redirectTo?: string;
  catalogConfiguration: CatalogConfiguration;
  announcements?: Announcements;
  // Food Bank Donation
  enableFoodBankDonation?: boolean;
  facebookCatalog?: {
    catalogId: string;
    productSetId: string;
    customConversionId: string;
    hasCatalog: boolean;
    lastExecution: Date;
    error: string;
    catalogUrl: string;
  };
  googleMerchant?: {
    csvUrl: string;
    feedId: string;
    lastExecution?: Date;
  };
  maxMultimediaRepositorySize: number;
  security: EstablishmentSecurity;
}

export type EstablishmentCheckout = {
  establishmentId: string;
  deliveryCost?: number;
  paymentMethods: PaymentMethod[];
  customFields?: CustomField[];
  created: Date;
  updated: Date;

  shippingOptionInPlace?: ShippingOptionInPlace;
  shippingOptionTakeAway?: ShippingOptionTakeAway;
  shippingOptionDelivery?: ShippingOptionDelivery;
  shippingOptionParcel?: ShippingOptionParcel;
};

export type EstablishmentSecurity = {
  mfaEnabled: boolean;
  mfaAdditionalContacts: {
    name: string;
    phone: string;
  }[];
};

export type Announcements = {
  title?: string;
  body?: string;
};

export type StockDisplayCondition = 'always' | 'never' | 'less-than-equal';

export type CatalogConfiguration = {
  enableCoupons: boolean;
  minimumPurchase: number;
  displaySku: boolean;
  skuInOrderMessage: boolean;
  stockDisplayCondition: StockDisplayCondition;
  stockDisplayConditionValue: number;
  displayProductPriceWithoutTaxes: boolean;
};

export interface EstablishmentProductsExport {
  enabled: boolean;
  sources: ProductsExportSource[];
}

export interface ProductsExportSource {
  type: ProductsExportSourceType;
  enabled: boolean;
  lastExecutionDate: Date | any;
  status: ProductsExportSourceStatus;
  error: string;
  url: string;
}

export enum ProductsExportSourceStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  ERROR = 'error',
}

export type ProductsExportSourceType = 'instagram' | 'googleMerchant';

export interface ExportJob {
  establishmentId: string;
  establishmentSlug: string;
  type: ProductsExportSourceType;
  createdAt: Date;
}

export type Feature =
  | 'DELIVERY_ZONES'
  | 'PARCEL_SHIPPING'
  | 'PRODUCTS_EXPORT'
  | 'ORDER_LIST_DETAILS'
  | 'PRODUCT_PRESENTATIONS'
  | 'PRODUCT_OPTIONS'
  | 'PRODUCT_GROUPS'
  | 'THEME'
  | 'QR_CODE'
  | 'DIGITAL_MENU'
  | 'COUPONS'
  | 'SEO_SOCIAL_MEDIA'
  | 'SLUG_CUSTOMIZATION'
  | 'FACEBOOK_PIXEL'
  | 'GOOGLE_ANALYTICS'
  | 'GOOGLE_TAG_MANAGER'
  | 'CHECKOUT_CUSTOM_FIELDS'
  | 'WORKING_HOURS'
  | 'STATISTICS_EXPLORE_RESULTS'
  | 'SUBDOMAIN'
  | 'IMAGE_GALLERY'
  | 'IMAGE_GALLERY_EXTENDED'
  | 'PRODUCT_ITEM_IMAGES'
  | 'WHITE_LABEL_BRANDING'
  | 'INTEGRATIONS'
  | 'INTEGRATIONS_PUNIYA'
  | 'INTEGRATIONS_PEDIDOS_YA'
  | 'INTEGRATIONS_RAPIBOY'
  | 'INTEGRATIONS_JELP'
  | 'INTEGRATIONS_CABIFY'
  | 'INTEGRATIONS_CORREO_ARGENTINO'
  | 'INTEGRATIONS_EPICK'
  | 'INTEGRATIONS_ARCA'
  | 'ALT_LANGUAGE'
  | 'STOCK_CONTROL'
  | 'BULK_EDIT'
  | 'PRODUCTS_IMPORT'
  | 'SKU'
  | 'CATEGORY_AVAILABILITY'
  | 'ANNOUNCEMENTS'
  | 'ADVANCED_STATISTICS'
  | 'THERMAL_PRINTING'
  | 'ADS'
  | 'PRODUCT_TAGS'
  | 'DYNAMIC_CATEGORIES'
  | 'USER_MANAGEMENT'
  | 'ORDER_EDITOR'
  | 'SECURITY';

export enum DeliveryType {
  FIXED = 'fixed',
  DELIVERY_ZONES = 'delivery-zones',
  TO_BE_DEFINED = 'to-be-defined',
  PROVIDERS = 'providers',
}

export type DeliveryOperation = 'OWN' | 'INTEGRATION';
export type QuotationMethod = 'MANUAL' | 'INTEGRATION';
export type DeliveryZoneType = 'circle' | 'polygon';
export type DeliveryZoneBase<T extends DeliveryZoneType> = {
  type: T;
  cost: number;
  minimumPurchase?: number;
  minimumPurchaseForFreeShipping?: number;
  color: string;
};
export type DeliveryZoneCircle = DeliveryZoneBase<'circle'> & {
  center: { lat: number; lng: number };
  radius: number;
};
export type DeliveryZonePolygon = DeliveryZoneBase<'polygon'> & {
  paths: { lat: number; lng: number }[];
};

export type DeliveryZone = DeliveryZoneCircle | DeliveryZonePolygon;
export type CustomFieldType = 'text' | 'number' | 'date' | 'single-choice' | 'multiple-choice';
export type CustomFieldOption = { value: string };

export interface CustomField {
  fieldType: CustomFieldType;
  name: string;
  legend?: string;
  required?: boolean;
  min?: number;
  max?: number;
  defaultValue?: number | string;
  options?: CustomFieldOption[];
}

export interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
  toDate: () => Date;
}

export interface CustomFieldDetail {
  field: CustomField;
  value?: string | number | Date | FirestoreTimestamp | string[];
}

export interface EstablishmentSeoSocialMedia {
  shortDescription?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  shortImage?: string;
  favicon?: string;
  displayShareButton?: boolean;
  // twitterUser?: string;
}

export type PaymentGateway = 'STRIPE' | 'BOXFUL' | 'OFFLINE' | 'ZENRISE';

export interface SubscriptionInfo {
  id: string;
  plan: string; // TODO: this should be typed PlayType
  createdAt: Date;
  updatedAt: Date;
  canceledAt?: Date | null;
  canceledByUser?: boolean;
  currentPeriodStartDate: Date | null;
  currentPeriodEndDate: Date | null;
  trialPeriodStartDate?: Date | null;
  trialPeriodEndDate?: Date | null;
  paymentGateway: PaymentGateway;
  isTrial: boolean;
  requiresPayment: boolean;
  lastPaymentError?: {
    message: string;
    code: string;
    /**
     * Bank error code
     */
    declineCode?: string;
  };
  status: SubscriptionStatus;
  customerId: string;
  countryCode?: Country;
  lastUpgrade?: Date;
  billingType?: BillingType;
  promotionCode?: string;
  requiresOnboarding?: boolean;
  establishmentDeletionType?: 'IMMEDIATELY' | 'END_OF_PERIOD';
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DELETED = 'deleted',
}

export interface ContactInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  discoverySource?: string;
  dni?: string;
  documentType?: 'DNI' | 'CUIT' | 'CUIL';
}

export type PaymentMethodPriceModifier = 'none' | 'extraCharge' | 'discount';

export interface PaymentMethod {
  removable: boolean;
  name: string;
  priceModifier: PaymentMethodPriceModifier;
  extraCharge?: number;
  discount?: number;
  active: boolean;
  type: PaymentMethodType;
  hasLink?: boolean;
  paymentLinkAvailable?: boolean;
  legend?: string;
  countryAvailables?: Country[];
  displayCashPaymentAmount?: boolean;
}

export enum PaymentMethodType {
  CASH = 'CASH',
  MERCADOPAGO = 'MERCADOPAGO',
  CARD = 'CARD',
  UALA_LINK = 'UALA_LINK',
  MERCADOPAGO_LINK = 'MERCADOPAGO_LINK',
  ZENRISE_LINK = 'ZENRISE_LINK',
}

export interface EstablishmentTheme {
  color1?: string;
  colorContrast1?: string;
  color2?: string;
  colorContrast2?: string;
  color3?: string;
  color4?: string;
}

export interface WorkingDays {
  isActive: boolean;
  enableOrdersOnClosed: boolean;
  selectedDaysOfWeek: (keyof WorkingDays['daysOfWeekConfig'])[];
  customizeHoursByDay: boolean;
  primaryWorkingHours?: TimeRange;
  secondaryWorkingHours?: TimeRange;
  daysOfWeekConfig: {
    1: WorkingHours;
    2: WorkingHours;
    3: WorkingHours;
    4: WorkingHours;
    5: WorkingHours;
    6: WorkingHours;
    7: WorkingHours;
  };
}

export type TemporaryClosure = {
  mode: 'now' | 'schedule' | 'disabled';
  from?: Date;
  to?: Date;
  message?: string;
  allowCatalogBrowsing?: boolean;
};

export interface WorkingHours {
  primaryWorkingHours: TimeRange;
  secondaryWorkingHours: TimeRange;
}

export interface TimeRange {
  from?: number;
  to?: number;
}

export type ProductGroupingStrategy = 'category' | 'product-group' | 'none';

export type Category = WithAltLanguages<
  {
    id: string;
    added: Date;
    updated: Date;
    establishmentId: string;
    image?: string;
    name: string;
    active: boolean;
    sortOrder: number;
    hideLabelInList?: boolean;
    availability: {
      isActive: boolean;
      isHidden: boolean;
      daysOfWeek: number[];
      timeRange: TimeRange;
    };
    isDynamic: boolean;
    dynamicGrouping?: ProductGroupingStrategy;
    linkedProductTagIds?: string[];
    productsOrder?: string[];
    imageToClone?: ImageToClone;
    clonedFrom?: string;
  },
  'name'
>;

export type Product = WithAltLanguages<
  {
    id: string;
    added: Date;
    updated: Date;
    categoryId: string;
    establishmentId?: string;
    description?: string;
    name: string;
    sku?: string;
    price: number;
    priceDiscount?: number;
    sortOrder: number;
    stockQty: number | null; // null is equal to "Infinite"; 0 is without stock; any number means finite stock
    aliquot?: AliquotAmount;
    hidden?: boolean;
    presentations?: OptionConfiguration | null;
    options?: OptionConfiguration[];
    linkedOptionIds?: string[]; // This is the list of Shared Options linked with this Product
    brand?: string;
    link?: string;
    categoryName?: string;
    groupName?: string;
    images: string[];
    linkedProductTagIds: string[];
    primaryTagId?: string;
    imagesToClone?: ImageToClone[];
    clonedFrom?: string;
    isOptionItem?: boolean;
    weight?: number;
    height?: number;
    length?: number;
    width?: number;
  },
  'name' | 'description' | 'groupName'
>;

export type ImageToClone = {
  srcPath: string;
  destPath: string;
};

export type ProductListItemDTO = Product & {
  searchableName: string;
  listIndex: number;
  isUpdatingImages: boolean;
};

export interface SharedOption {
  id?: string;
  establishmentId: string;
  linkedItemIds: string[];
  sortOrder: number;
  details: OptionConfiguration;
  clonedFrom?: string;
}

export type SharedOptionWithItems = {
  sharedOption: SharedOption;
  linkedItems: Product[];
};

export type OptionConfigurationType = 'customization' | 'extras';

export type OptionConfiguration = WithAltLanguages<
  {
    id: string;
    name: string;
    type?: OptionConfigurationType;
    rangeType: ProductOptionRangeType;
    min: number;
    max?: number;
    displayItemQuantities?: boolean;
    items?: ProductOptionItem[];
    sortOrder?: number;
    created?: Date;
    updated?: Date;
  },
  'name'
>;

export enum ProductOptionRangeType {
  ANY = 'any',
  FIXED = 'fixed',
  RANGE = 'range',
}

export type AliquotAmount = number | 'exempt' | null;
export type ProductOptionItem = WithAltLanguages<
  {
    id: string;
    name: string;
    sku?: string;
    isOptionItem?: boolean;
    image?: string;
    price: number;
    priceDiscount?: number;
    maxSelection?: number;
    quantity?: number;
    description?: string;
    stockQty: number | null; // null is equal to "Infinite"; 0 is without stock; any number means finite stock
    aliquot?: AliquotAmount;
    sortOrder?: number;
    weight?: number;
    height?: number;
    length?: number;
    width?: number;
    type?: ShippingItemType;
  },
  'name' | 'description'
>;

export type CartItemCategory = Pick<
  Category,
  'id' | 'establishmentId' | 'image' | 'name' | 'availability'
>;
export type CartItemProduct = Pick<
  Product,
  | 'id'
  | 'categoryId'
  | 'establishmentId'
  | 'description'
  | 'name'
  | 'sku'
  | 'price'
  | 'priceDiscount'
  | 'stockQty'
  | 'aliquot'
  | 'groupName'
  | 'images'
  | 'linkedProductTagIds'
  | 'weight'
  | 'height'
  | 'length'
  | 'width'
  | 'presentations' // NOTE: this shouldn't be necessary but is needed by some dependency during purchase process
>;

export interface CartItem {
  id: string;
  product: CartItemProduct;
  category: CartItemCategory;
  selectedPresentation?: CartItemOptionItems;
  selectedOptions?: CartItemOptionItems[];
  observations: string;
  qty: number;
}

export interface CartItemOptionItems {
  option: OptionConfiguration;
  items: ProductOptionItem[];
}

export interface PersonalInformation {
  name?: string | null;
  address?: Address | null;
  phone?: string | null;
  email?: string | null;
}

export interface CustomAddressComponents {
  freeSearch?: string;
}

export interface Address {
  street?: string | null;
  addressDetails?: AddressDetails | null;
  placeType?: AddressPlaceType | null;
  placeName?: string | null;
  lot?: string | null;
  floorAndNumber?: string | null;
  references?: string | null;
}

export type AddressPlaceType = 'house' | 'building' | 'business' | 'housing-state' | 'other';

export interface AddressDetails {
  freeSearch?: string;
  street: string;
  streetNumber: string;
  neighborhood?: string;
  state?: string;
  locality?: string;
  country?: string;
  postalCode?: string;
  fullAddress: string;
  coordinates?: google.maps.LatLngLiteral;
  useCustomLocation?: boolean;
  url?: string;
  timezone?: string;
  suggestedPlaceType?: AddressPlaceType;
  suggestedPlaceName?: string;
  resultType?:
    | 'locality'
    | 'administrativeArea'
    | 'houseNumber'
    | 'intersection'
    | 'street'
    | 'place';
}

export type ShippingOptionBase<T extends TypesOfShippings> = {
  active: boolean;
  name: string; // NOTE: probably this should be removed, but unkown implications
  type: T;
  allowedPaymentMethods: string[] | ['ALL'];
};

export type ShippingOptionTakeAway = ShippingOptionBase<TypesOfShippings.TAKE_AWAY>;
export type ShippingOptionInPlace = ShippingOptionBase<TypesOfShippings.IN_PLACE>;
export type ShippingOptionDelivery = ShippingOptionBase<TypesOfShippings.DELIVERY> & {
  deliveryOperation?: DeliveryOperation;
  deliveryType?: DeliveryType;
  minimumPurchase?: number;
  minimumPurchaseForFreeShipping?: number;
  deliveryCost?: number;
  deliveryZones?: DeliveryZone[];
  outOfDeliveryZoneShippingAllowed?: boolean;
  disableAddressInMap?: boolean;
};
export type ShippingOptionParcel = ShippingOptionBase<TypesOfShippings.PARCEL> & {
  quotationMethod: QuotationMethod;
  minimumPurchase?: number;
  minimumPurchaseForFreeShipping?: number;
  disableAddressInMap?: boolean;
};
export type ShippingOption =
  | ShippingOptionTakeAway
  | ShippingOptionInPlace
  | ShippingOptionDelivery
  | ShippingOptionParcel;

export type EndOrderDetailPaymentStatus =
  | 'PENDING'
  | 'PAID'
  | 'REFUNDED'
  | 'REJECTED'
  | 'CANCELLED';

export type EndOrderDetailPaymentLink = {
  status: EndOrderDetailPaymentStatus;
  id?: string;
  url?: string;
  updated?: Date;
};

export type EndOrderSource = 'app' | 'admin';

// a.k.a ordersnapshot
export type EndOrderDetails = {
  id?: string;
  sequenceId: number;
  status: OrderStatus;
  created: Date;
  updated: Date;
  establishmentId: string;
  personalInformation: PersonalInformation;
  billingInformation?: BillingInformation | null;
  taxVoucherSummary?: TaxVoucherSummary | null;
  cartItems: CartItem[];
  totalAmount: number;
  totalAmountWithDiscount: number;
  totalProductDiscount: number;
  deliveryCost: number;
  couponDiscount?: number;
  paymentMethodExtraCharge?: number;
  paymentMethodDiscount?: number;
  finalAmount?: number;
  couponConfigurationSnapshot?: CouponConfigurationResponse;
  // @deprecated - payment link is generated dynamically on the BE
  customFieldDetails?: CustomFieldDetail[];
  outOfDeliveryZone?: boolean;
  paymentMethod?: PaymentMethod | null;
  shippingOption?: ShippingOption | null | undefined;
  tableNumber?: string | null | undefined;
  howMuch?: number | null | undefined;
  userUid?: string;
  cancelReason?: OrderCancelReason;
  cancelReasonDetails?: string;
  cancelDate?: Date;
  internalNotes?: string;
  tags: string[];
  currencyCode: CurrencyCode;
  retries?: number;
  paymentLink?: EndOrderDetailPaymentLink | null;
  // Food Bank DOnation
  foodBankDonationAmount?: number;
  // Parcel tracking email
  parcelTrackingEmail?: string | null;
  /*
    5: adds sequenceId
    7: adds status, cancelReason, cancelReasonDetails, tags, internalNotes
  */
  schemaVersion: number;
  shippingEstimate?: ShippingEstimate;
  shippingOrder?: ShippingOrder;
  source: EndOrderSource;
  copiedFrom?: string;
};

export interface BillingInformation {
  provider: string;
  vatCondition: string | number;
  name?: string | null;
  documentType?: number;
  documentNumber?: number | null;
  address?: string | null;
  email?: string | null;
}

export type TaxVoucherOperation = 'bill' | 'credit' | 'pending';
export type TaxVoucherPrintCopies = 'single' | 'double' | 'triple';
export type TaxVoucherOptions = {
  groupingConceptName?: string;
  groupByOrder?: boolean;
  printCopies?: TaxVoucherPrintCopies;
};
export interface TaxVoucher {
  id: string;
  establishmentId: string;
  provider: string;
  details: {
    operation: TaxVoucherOperation;
    type: string | number;
    orderIds: string[];
    orderSequenceIds: number[];
    voucherRef: string;
    lineItemsByOrder: { orderId: string; lineItems: any[] }[];
    buyerBillingInfo: BillingInformation;
    sellerInfoExtra: {
      logoUrl: string;
      fantasyName: string;
    };
    accountDetails: {
      autoSendInvoice: boolean;
      supportEmail: string;
    };
    defaultAliquot?: AliquotAmount;
    taxVoucherOptions?: TaxVoucherOptions;
    observations?: string;
    voucherTotals: TaxVoucherTotals;
    currency: CurrencyCode;
    exchangeRate: number;
    associatedTaxVoucherSummary?: TaxVoucherSummary | null;
    created: Date;
    updated: Date;
  };
}
export interface TaxVoucherEmailSender {
  taxVoucherId: string;
  establishmentId: string;
  error?: string;
  details: {
    email: string;
    templateParams: {
      establishmentName: string;
      supportEmail: string;
    };
    created: Date;
    ttlDate: Date;
  };
}
export type TaxVoucherCreationParams = {
  establishmentId: string;
  orderIds: string[];
  billingInformation: BillingInformation;
  groupingConceptName?: string;
  groupByOrder?: boolean;
};

export type TaxVoucherTotals = {
  exemptAmount: number; // TODO: remove this field once voucherTotals is used
  vatAmount: number; // TODO: remove this field once voucherTotals is used
  tributesAmount: number; // TODO: remove this field once voucherTotals is used
  netAmount: number; // TODO: remove this field once voucherTotals is used
  totalAmount: number; // TODO: remove this field once voucherTotals is used
  vatDetails?: {
    // TODO: remove this field once voucherTotals is used
    id: number;
    aliquot: number;
    baseAmount: number;
    vatAmount: number;
  }[];
};

export type TaxVoucherSummary = {
  id: TaxVoucher['id'];
  establishmentId: TaxVoucher['establishmentId'];
  provider: TaxVoucher['provider'];
  associatedTaxVoucherId?: TaxVoucher['id'];
  associatedTaxVoucherRef?: TaxVoucher['details']['voucherRef'];
  details: Pick<
    TaxVoucher['details'],
    'operation' | 'type' | 'orderIds' | 'voucherRef' | 'voucherTotals' | 'currency' | 'created'
  >;
};

export interface Order {
  id?: string;
  sequenceId: number;
  status: OrderStatus;
  establishmentId: string;
  totalAmount: number;
  currencyCode: CurrencyCode;
  phone: number;
  date: Date;
  updated: Date; // NOTE: this was added after ~2024-03-15, so this field is null in Orders prior to this change
  address: string;
  paymentMethod: string;
  shippingOption?: string;
  name: string;
  schemaVersion: number;
  addressDetails: AddressDetails;
  tags?: string[];
  internalNotes?: string;
  cancelReason?: OrderCancelReason;
  cancelReasonDetails?: string;
  paymentLink?: EndOrderDetailPaymentLink;
  shippingEstimate?: ShippingEstimate;
  shippingOrder?: ShippingOrder;
  source: EndOrderSource;
  userUid?: string;
  taxVoucherSummary?: TaxVoucherSummary | null;
}

export type OrderItemLineItem = {
  categoryName: string;
  groupName: string;
  name: string;
  quantity: number;
  unitPrice: number;
  aliquot?: AliquotAmount;
  total: number;
  sku?: string;
  type: 'product' | 'presentation' | 'option-item' | 'group' | 'price-modifier';
};

export const orderStatuses = ['new', 'in-progress', 'completed', 'canceled'] as const;
export type OrderStatus = (typeof orderStatuses)[number];

export const orderCancelReasons = [
  'CUSTOMER_CANCELATION',
  'PRODUCTS_UNAVAILABLE',
  'WRONG_ADDRESS',
  'PAYMENT_ERROR',
  'DUPLICATED',
  'OTHER',
] as const;
export type OrderCancelReason = (typeof orderCancelReasons)[number];

export type OrdersSequence = {
  lastValue: number;
  createdAt: Date;
  updatedAt: Date;
};

export interface DateRange {
  from: Date;
  to: Date;
}

export interface StatisticsSummary {
  numOfSales: number;
  salesAmount: number;
  averageTicketAmount: number;
  uniqueCustomers: number;
  paymentMethod: {
    name: string;
    amount: number;
  };
  shippingOption: {
    name: string;
    amount: number;
  };
}

export enum CouponDiscountType {
  FIXED = 'FIXED',
  PERCENTAGE = 'PERCENTAGE',
}

export interface CouponConfiguration {
  id: string;
  name: string;
  used: number;
  code: string;
  validFrom: Date;
  validTo: Date;
  active: boolean;
  discountType: CouponDiscountType;
  discount: number;
  quantity?: number;
  minAmount?: number;
  paymentMethods: string[] | ['ALL'];
  daysOfWeek: number[] | ['ALL'];
  categories: string[] | ['ALL'];
  productTags: string[];
  added: Date;
  updated: Date;
}

export type CouponValidationErrorCode =
  | 'NOT_FOUND'
  | 'INACTIVE'
  | 'PENDING'
  | 'EXPIRED'
  | 'SOLD_OUT';

export interface CouponUsageRegistry {
  couponConfigurationId: string;
  establishmentId: string;
  couponCode: string;
  details: {
    orderId: string;
    ordeAmount: number;
    discountAmount: number;
    userName: string;
    userPhone: string;
    date: Date;
  };
}

export type CouponConfigurationResponse = Pick<
  CouponConfiguration,
  | 'id'
  | 'code'
  | 'discountType'
  | 'discount'
  | 'daysOfWeek'
  | 'categories'
  | 'productTags'
  | 'paymentMethods'
  | 'minAmount'
  | 'updated'
>;

export interface PrivateConfigurationPaymentLink {
  clientId: string | null;
  secretId: string | null;
}
export interface PrivateConfiguration {
  mercadopagoToken: string | null;
  mercadopagoRefreshToken?: string | null;
  zenriseConfig: PrivateConfigurationPaymentLink | null;
  ualaConfig: UalaConfig | null;
}

export enum StatisticsType {
  NUM_OF_SALES = 'numOfSales',
  SALES_AMOUNT = 'salesAmount',
  AVERAGE_TICKET_AMOUNT = 'averageTicketAmount',
  UNIQUE_CUSTOMERS = 'uniqueCustomers',
  PAYMENT_METHODS = 'paymentMethod',
  SHIPPING_OPTIONS = 'shippingOption',
}

export type StatisticsTypes = 'basic' | 'advanced';

export type StatisticsAdvancedTypes = 'orders' | 'orderDetails' | 'googleAnalytics';

export interface FunctionResponseOK<T> {
  result: T;
}

export interface FunctionResponseError {
  error: {
    status: string;
    message: string;
    details?: any;
  };
}

export type FunctionResponse<T = any> = FunctionResponseOK<T> | FunctionResponseError;
export type FunctionResponseClient<T = any> = T | FunctionResponseError;

export interface ValidateCouponParams {
  establishmentId: string;
  couponCode: string;
}

export interface UseCouponParams {
  couponCode: string;
  establishmentId: string;
  userName: string;
  userPhone: string;
  couponDiscount: number;
  orderAmount: number;
}

export interface RegisterData {
  name: string;
  slug: string;
  username: string;
  password: string;
}

export const CURRENCY_CODES = [
  'ARS',
  'BOB',
  'BRL',
  'BSD',
  'CAD',
  'CLP',
  'COP',
  'CRC',
  'CUP',
  'DOP',
  'EUR',
  'GTQ',
  'GYD',
  'HNL',
  'JMD',
  'MXN',
  'NIO',
  'PAB',
  'PEN',
  'PYG',
  'USD',
  'UYU',
  'VES',
] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];
export type Country = 'AR' | 'CL' | 'MX' | 'PE' | 'UY' | 'CO' | 'BR' | 'ES' | 'XX';
export type LocaleID =
  | 'es-AR'
  | 'es-CL'
  | 'es-MX'
  | 'es-PE'
  | 'es-UY'
  | 'es-CO'
  | 'pt'
  | 'es'
  | 'es-419'
  | 'en-US';
export type CurrencyLocaleData = {
  currencyCode: CurrencyCode;
  currencySymbol: string;
  decimalsLength: number;
  thousandsSeparator: '.' | ',' | ' ';
  decimalSeparator: '.' | ',';
  excelNumberFormat: string;
};

export type REPORT_TYPE = 'ORDERS' | 'PRODUCTS';

export interface Customer {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface RegistrationData {
  id?: string;
  created?: Date;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dni?: string;
    documentType?: 'DNI' | 'CUIT' | 'CUIL';
    discoverySource: string;
  };
  establishment: {
    name: string;
    slug: string;
    phone: string;
    countryCode: Country;
    countryDetail: string;
    categories: string[];
    cuit?: string;
    businessName?: string;
    ivaCondition?: string;
  };
  account: {
    username: string;
    password: string;
    email?: string;
  };
  seller?: string;
  couponId?: string;
  promotionCode?: string;
  planType?: PlanType;
  billingType: BillingType;
  acceptTermsAndConditions?: boolean;
}

export interface StripePromotionCode {
  discount: number;
  recurring_profit: number;
  couponId: string;
}

export interface StripeSubscriptionParams {
  registrationData: Partial<RegistrationData>;
  priceId: string;
  paymentMethodId: string;
  planType: PlanType;
}

export interface StripeSubscriptionResponse {
  subscription: any;
}

export interface StripeCancelSubscriptionParams {
  establishmentId: string;
  subscriptionId: string;
}

export interface StripeCancelSubscriptionResponse {
  result: boolean;
}

export interface MercadopagoLinkResponse {
  mercadopagoLink: string;
}

export interface MercadopagoLinkParams {
  establishmentId: string;
  totalAmount: string;
}

export interface BoxfulCustomer {
  id?: number;
  fname: string;
  lname: string;
  email: string;
  phone: number;
  custom_fields: BoxfulCustomFields[];
}

export interface BoxfulCustomFields {
  name: string;
  field_type: string;
  content_string: string;
}

export interface BoxfulCustomerCreateResponse {
  loginToken: string;
}

export interface BoxfulCreateCustomerParams {
  boxfulCustomer: BoxfulCustomer;
}

export interface BoxfulWebhook {
  subscription: BoxfulSubscription;
  customer: BoxfulCustomer;
}

interface BoxfulSubscription {
  object: string;
  id: number;
  customer_id: number;
  status: string;
  payment_type: string;
  created_at: string;
  updated_at: string;
  amount: number;
  currency: string;
  coupon_id?: any;
  plan_id: number;
  delivery_price_item_id: number;
  external_service: string;
  metadata?: any;
  authorized_at?: any;
  cancelled_at: string;
  cbu?: any;
  last_invoice: Lastinvoice;
  next_invoice?: any;
}

interface Lastinvoice {
  object: string;
  id: number;
  status: string;
  amount: number;
  due_date: string;
  currency: string;
}

export interface ZenriseCancelSubscription {
  establishmentId: string;
  subscriptionId: string;
}

export interface ZenriseSubscriptions {
  subscriptions: ZenriseSubscription[];
}

export type ZenrisePaymentLink = {
  amount: number;
  contact: {
    firstName: string;
  };
  description: string;
  firstDueDate: string;
  percentageAfterFirstDueDate: number;
  secondDueDate: string;
  percentageAfterSecondDueDate: number;
  sendPerEmail: boolean;
  backUrl: {
    success: string;
    failure: string;
  };
};

interface ZenriseSubscription {
  contact: ZenriseSubscriptionContact;
  externalReference: string;
  startDate: string;
  paymentToken: ZenriseSubscriptionPaymentToken;
  collectType: string;
  planId?: any;
  creditCard: number;
  metadata: {
    registrationData: RegistrationData;
  };
}

interface ZenriseSubscriptionPaymentToken {
  bin: string;
  token: string;
  lastFourDigits: string;
}

interface ZenriseSubscriptionContact {
  email: string;
  firstName: string;
  lastName: string;
  holderDocumentNumber: string;
  documentType?: 'DNI' | 'CUIT' | 'CUIL';
  phoneAreaCode: string;
  phoneNumber: string;
  externalReference: string;
}

export interface ZenriseWebhook {
  event: string;
  object: ZenriseWebhookObject;
}

export interface ZenriseWebhookObject {
  id: number;
  status: string;
  description?: any;
  subscriptionConfiguration: ZenriseWebhookConfiguration;
  contact: ZenriseWebhookContact;
  metadata: any;
  billingDates: ZenriseWebhookBillingDate;
}

interface ZenriseWebhookContact {
  id: number;
  externalReference: string;
}

interface ZenriseWebhookConfiguration {
  id: number;
  startDate: string;
  periods: number;
  amountAfterFirstDueDate?: any;
  amountAfterSecondDueDate?: any;
  firstDuePercentage: number;
  secondDuePercentage: number;
  endDate: string;
  lastFourDigits: string;
  collectType: string;
  chargeType: string;
  daysAfterFirstDue: number;
  daysAfterSecondDue: number;
}

interface ZenriseWebhookBillingDate {
  startBillingDate: string;
  nextBillingDate: string;
}

export interface AdminPanelOption {
  type: AdminPanelOptionType;
  icon: string;
  url: string;
  isVisible: boolean;
  label: string;
  class?: string;
}

export type AdminPanelOptionType =
  | 'catalogue'
  | 'general'
  | 'subscription'
  | 'theme'
  | 'seoSocialMedia'
  | 'workingHours'
  | 'paymentMethods'
  | 'shippingOptions'
  | 'checkout'
  | 'qrCode'
  | 'coupons'
  | 'statistics'
  | 'orderList'
  | 'analytics'
  | 'instagramShopping'
  | 'integrations'
  | 'electronicBilling'
  | 'clonning'
  | 'announcements'
  | 'ads'
  | 'invitedUsers'
  | 'security';

export type AdminPanelLinksNavigation =
  | 'subscriptionInfo'
  | 'youtube'
  | 'web'
  | 'instagram'
  | 'subscriptionUpgrade'
  | 'helpCenter';

export type AdminPanelHeaderNavigation = 'settings' | 'previewMode' | 'notifications' | 'user';

export enum PlanType {
  FREE = 'FREE',
  BASIC = 'BASIC',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
  CUSTOM = 'CUSTOM',
}
export interface ProrationRequest {
  establishmentId: string;
  targetPlan: PlanType;
  prorationDate?: number;
  billingType: BillingType;
}

export interface UpdateSubscriptionRequest {
  establishmentId: string;
  targetPlan: PlanType;
  currentPlan: PlanType;
  customerId: string;
  billingType: BillingType;
  prorationDate?: number;
  paymentMethodId?: string;
  startNow?: boolean;
}

export interface Plan {
  description?: string;
  name?: string;
  prices?: any;
  features?: string[];
  amounts?: any;
}

export interface ProrationResponse {
  result: ProrationAmount;
}

export interface ProrationAmount {
  unusedCurrentPlanAmount: number;
  differenceToPayForNewPlan: number;
  targetPlanPrice: number;
  remainingDays?: number;
}

export interface ZenriseUpdateSubscriptionRequest {
  subscriptionId: number;
  planId: number;
  startNow: boolean;
}

export interface ZenriseProrationResponse {
  prorationAmount: number;
  transcurredDays: number;
  remainingDays: number;
  currentPrice: number;
  newPrice: number;
  type: string;
}

export type PlanFeature = {
  name: string;
  planDetails: {
    [key: string]: PlanDetails;
  };
};

export type PlanDetails = {
  display: boolean;
  text?: string;
  custom?: boolean;
  config?: any;
};

export interface PasswordRecoveryRequest {
  id?: string;
  date: Date;
  establishmentSlug?: string;
  registrationEmail: string;
  dateUsed?: Date;
  isInvitedUser?: boolean;
}

export type AppRequestUrl = {
  hostname: string;
  pathname: string;
};

export interface CancelSubscriptionReason {
  values: string[];
  comment?: string;
  slug: string;
  establishmentId: string;
}

export type BillingType = 'MONTLY' | 'YEARLY';

export interface StripePromotionCode {
  discount: number;
  recurring_profit: number;
  couponId: string;
}

export interface StripePromotionCodeSuccess {
  couponId: string;
  promotionCode: string;
}

export interface LandingPromotions {
  startDate: Date;
  endDate: Date;
  promotionCode: string;
  codes: LandingPromotionsCode[];
}

export interface LandingPromotionsCode {
  billingType: BillingType;
  code: string;
  discount: number;
  quantityOfMonths: number;
}

export type SortInput = { id: string; sortOrder: number };

export type CustomDomain = {
  domain: string;
  provider: string;
};

export const LANGUAGE_COOKIE = 'pedix-language';
export const LANGUAGE_QUERY = 'lang';
export const DEFAULT_LANGUAGE = 'es' as const;
export type ALT_LANGUAGES = 'en';
export type SUPPORTED_LANGUAGES = typeof DEFAULT_LANGUAGE | ALT_LANGUAGES;
export type AVAILABLE_LANGUAGES =
  | [SUPPORTED_LANGUAGES]
  | [SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGES];

export type WithAltLanguages<T, Keys extends string> = T & {
  [Prop in keyof T as Prop extends Keys ? `${Prop}_${ALT_LANGUAGES}` : never]?: T[Prop];
};

export type CloneCatalogOptions = {
  overwriteVisibility: boolean;
  overwriteStock: boolean;
  cloneIntegrationRelations?: boolean;
};

export type CloneCatalogParams = {
  originSlug: string;
  targetSlug: string;
  cloneOptions: CloneCatalogOptions;
};

export type BulkEditEntity = 'products' | 'option-items';

export type ProductImportExcelData = {
  productId: string;
  itemType: 'product' | 'presentation';
  name: string;
  sku?: string;
  image: string;
  multimediaFileUri?: string;
  categoryName: string;
  categoryId: string;
  groupName?: string;
  description?: string;
  itemId?: string;
  presentationName?: string;
  presentationDescription?: string;
  presentationSelectionQty?: number | null; // Any number equals RangeType.FIXED; null equals RangeType.ANY
  price: number;
  priceDiscount?: number | null;
  aliquot?: AliquotAmount;
  stockQty: Product['stockQty'];
  status: 'ACTIVO' | 'INACTIVO';
  tagList: string;
  primaryTagName?: string;
  rowNumber?: number;
  isOptionItem: boolean;
  productRef: Product;
  // Dimensiones físicas para envíos
  weight?: number;
  height?: number;
  length?: number;
  width?: number;
};

export type ProductImportExcelDataForValidation = Omit<
  ProductImportExcelData,
  'tagList' | 'multimediaFileUri'
> & {
  tagList: string[] | null;
  multimediaFileUri: string[] | null;
};

export type ProductGridData = {
  id: string;
  name: string;
  image: string;
  multimediaFileUris?: string[];
  categoryId: string;
  categoryName: string;
  groupName?: string;
  description?: string;
  hidden: boolean;
  presentationSelectionQty?: number | null;
  productTags: ProductTag[];
  primaryTag?: ProductTag;
  items: {
    itemType: 'product' | 'presentation';
    id: string;
    name: string;
    sku?: string;
    description: string | undefined;
    price: number;
    priceDiscount?: number;
    aliquot?: AliquotAmount;
    stockQty: Product['stockQty'];
    savingPrice?: boolean;
    savingPriceDiscount?: boolean;
    savingStockQty?: boolean;
    // Dimensiones físicas para envíos
    weight?: number;
    height?: number;
    length?: number;
    width?: number;
    savingWeight?: boolean;
    savingHeight?: boolean;
    savingLength?: boolean;
    savingWidth?: boolean;
  }[];
  searchableItemsPresentation: string;
  searchableItemsSku: string;
  searchableItemsPrice: string;
  searchableItemsPriceDiscount: string;
  searchableItemsAliquot: string;
  searchableItemsStock: string;
  searchableItemsWeight: string;
  searchableItemsHeight: string;
  searchableItemsLength: string;
  searchableItemsWidth: string;
  isOptionItem: boolean;
  productRef: Product;
  operation?: 'create' | 'update';
};

export type ProductImportValidationResult = {
  rowNumber: number;
  productData: ProductImportExcelData;
  isValid: boolean;
  errors?: ErrorObject[];
};

export type BulkActionType =
  | 'price'
  | 'priceDiscount'
  | 'stockQty'
  | 'group'
  | 'status'
  | 'category'
  | 'delete';

export type ZenriseDebtResponse = {
  status: string;
  message: string;
  data: ZenriseDebtData;
};

export type ZenriseDebtData = {
  daysRemaining: number;
  debt: number;
  total: number;
  priceCurrentPlan: number;
  subscription: SubscriptionInfo;
  slug: string;
};

export interface ZenriseReactivateSubscription {
  subscriptionId: number;
  collectPendingInvoice: boolean;
  extraDays: number;
  creditCard: boolean;
  paymentToken: ZenriseReactivateSubscriptionPaymentToken;
}
interface ZenriseReactivateSubscriptionPaymentToken {
  bin: string;
  token: string;
  lastFourDigits: string;
  cardDueDate: string;
}

export interface MercadopagoTokenResponse {
  success: boolean;
  message: string;
  data?: any;
}

export type PaymentLinkResponse = {
  paymentLink: string;
  id?: string;
};

export type UalaConfig = {
  user_name: string;
  client_id: string;
  client_secret_id: string;
  grant_type: string;
};

export type CfIpLocation = {
  country: string;
  city: string;
  continent: string;
  latitude: string;
  longitude: string;
  postalCode: string;
  metroCode: string;
  region: string;
  regionCode: string;
  timezone: string;
};

export type ExchangeRateResponse = {
  moneda: string;
  casa: string;
  nombre: string;
  compra: number;
  venta: number;
  fechaActualizacion: string;
};

export type Campaign = {
  campaignId: string;
  id?: string;
  adSetId: string;
  adId: string;
  establishmentId: string;
  establishmentName: string;
  establishmentSlug: string;
  budget: number;
  startDate: Date;
  endDate: Date;
  createdAt: Date;
  comments?: string;
  notificationSent?: boolean;
  activeDays: number;
  destination: 'STORE' | 'WHATSAPP';
  gender?: number[];
  ageMin?: number;
  ageMax?: number;
  categoryIds?: string[];
  paymentMethodId: string;
  // Campos adicionales para los datos de cada paso
  paymentIntentId?: string;
  catalogId?: string;
  catalogUrl?: string;
  feedId?: string;
  feedStatus?: string;
  productSetId?: string;
  customConversionId?: string;
  currentStep?: CampaignSteps;
  error?: string;
  lastUpdated?: Date;
  nextStep?: CampaignSteps;
  paymentCurrency?: 'USD' | 'ARS';
  exchangeRate?: number;
  totalAmountARS?: number;
  paymentInfo?: {
    gateway: 'stripe' | 'zenrise';
    // Para Stripe
    paymentIntentId?: string;
    clientSecret?: string;
    requires3DSecure?: boolean;
    // Para Zenrise
    paymentMethod?: string;
    paymentId?: number;
    amountPayed?: string;
    status?: string;
    invoiceId?: number;
    // Común
    currency?: 'USD' | 'ARS';
    exchangeRate?: number;
    totalAmountARS?: number;
  };
};

export enum CampaignSteps {
  CAMPAIGN_CREATION = 'CAMPAIGN_CREATION',
  PAYMENT = 'PAYMENT',
  PAYMENT_3D_SECURE_PENDING = 'PAYMENT_3D_SECURE_PENDING',
  CATALOG_SETUP_PENDING = 'CATALOG_SETUP_PENDING',
  CATALOG_SETUP_CREATE_CATALOG = 'CATALOG_SETUP_CREATE_CATALOG',
  CATALOG_SETUP_CREATE_FEED = 'CATALOG_SETUP_CREATE_FEED',
  CATALOG_SETUP_CHECK_FEED = 'CATALOG_SETUP_CHECK_FEED',
  CATALOG_SETUP_DEFAULT_PRODUCT_SET = 'CATALOG_SETUP_DEFAULT_PRODUCT_SET',
  CATALOG_SETUP_CUSTOM_CONVERSION = 'CATALOG_SETUP_CUSTOM_CONVERSION',
  CAMPAIGN_PRODUCT_SET_CREATION = 'CAMPAIGN_PRODUCT_SET_CREATION',
  FB_CREATE_CAMPAIGN_OBJECT = 'FB_CREATE_CAMPAIGN_OBJECT',
  FB_CREATE_ADSET_OBJECT = 'FB_CREATE_ADSET_OBJECT',
  FB_CREATE_AD_OBJECT = 'FB_CREATE_AD_OBJECT',
  FINALIZE_CAMPAIGN_DETAILS = 'FINALIZE_CAMPAIGN_DETAILS',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
}

export interface CampaignRequest {
  establishmentId: string;
  establishmentName: string;
  establishmentSlug: string;
  budget: number;
  activeDays: number;
  comments?: string;
  gender?: number[];
  ageMin?: number;
  ageMax?: number;
  categoryIds?: string[];
  paymentMethodId: string;
  destination?: 'STORE' | 'WHATSAPP';
  paymentCurrency?: 'USD' | 'ARS' | null;
  exchangeRate?: number;
  totalAmountARS?: number;
  cardData?: {
    cardNumber: string;
    cardName: string;
    expirationMonth: string;
    expirationYear: string;
    cvv: string;
    dni: string;
    creditCard: number; // 0=débito, 1=crédito
  };
}

export interface CampaignGrid {
  id?: string;
  createdAt: Date;
  adId: string;
  comments: string;
  endDate: Date;
  campaignId: string;
  adSetId: string;
  startDate: Date;
  establishmentId: string;
  budget: number;
  destination?: 'STORE' | 'WHATSAPP';
  facebookCampaign: CampaignGridFacebook;
  currentStep: CampaignSteps;
}

export interface CampaignGridFacebook {
  name: string;
  status: string;
  startDate: Date;
  endDate: Date;
  insights: CampaignGridFacebookInsights;
}

export interface CampaignGridFacebookInsights {
  spend: number;
  impressions: number;
  clicks: number;
  customConversionValue: number;
}
export type PricingError = {
  id: string;
  type: 'product' | 'presentation' | 'option-item';
  name: string;
  oldPrice: number;
  oldPriceDiscount: number;
  newPrice: number;
  newPriceDiscount: number;
  parentItem?: {
    id: string;
    name: string;
  };
};

export type ProductTag = {
  id: string;
  establishmentId: string;
  sortOrder: number;
  clonedFrom?: string;
  details: WithAltLanguages<
    {
      tag: string;
      color: string;
      created: Date;
      updated: Date;
    },
    'tag'
  >;
};

export type PackageType = {
  id: string;
  establishmentId: string;
  name: string;
  length: number;
  width: number;
  height: number;
  maxWeight: number;
  tareWeight: number;
  isDefault?: boolean;
  sortOrder: number;
  created: Date;
  updated: Date;
  clonedFrom?: string;
};

export type CalculateShipping = {
  establishmentId: string;
  endOrderDetails: EndOrderDetails;
};

export type ShippingInfo = {
  name: string;
  phone: string;
  address: Address;
};

export type ShippingEstimateRequest = {
  establishmentId: string;
  items: ShippingItem[];
  pickup: ShippingInfo;
  delivery: ShippingInfo;
  total: number;
  currencyCode: CurrencyCode;
};

export type ShippingItemType = 'COLD' | 'STANDARD' | 'FRAGILE';

export type ShippingItem = {
  description?: string;
  quantity: number;
  value: number;
  weight: number;
  height: number;
  length: number;
  width: number;
};

export type ShippingEstimate = {
  estimateId: string;
  providerName: string;
  offerId: string;
  price: number;
  deliveryTime: string;
  additionalInfo?: string;
  deliveryMode?: string;
  confirmationTimeLimit?: string;
  error?: string;
  outOfDeliveryZone?: boolean;
  blockOrder?: boolean;
  date: Date;
  // Campos específicos para Mi Correo
  deliveryType?: 'D' | 'S'; // D = Domicilio, S = Sucursal
  productType?: string; // CP = Correo Postal, etc.
  agency?: string; // Código de sucursal (obligatorio solo para deliveryType 'S')
};

export type ShippingStatus =
  | 'CONFIRMED'
  | 'CANCELLED'
  | 'IN_PROGRESS'
  | 'NEAR_PICKUP'
  | 'PICKED_UP'
  | 'NEAR_DROPOFF'
  | 'COMPLETED';

export type ShippingOrder = {
  confirmationCode: string;
  createdAt: Date;
  updatedAt: Date;
  status: ShippingStatus;
  providerName: string;
  shippingId: string;
  offerId?: string;
  estimatedDeliveryTime: string;
  distance?: number;
  deliveryMode?: string;
  trackingUrl?: string;
  supportUrl?: string;
  additionalInfo?: string;
  cancelCode?: string;
  cancelReason?: string;
  verificationCode?: string;
};

export type ShippingOrderHistory = {
  orderId: string;
  shippingOrder: ShippingOrder;
};

export type PedidosYaWebhookPayload = {
  topic: string;
  id: string;
  referenceId: string;
  generated: string;
  transmitted: string;
  data: {
    status:
      | 'CONFIRMED'
      | 'CANCELLED'
      | 'IN_PROGRESS'
      | 'NEAR_PICKUP'
      | 'PICKED_UP'
      | 'NEAR_DROPOFF'
      | 'COMPLETED';
    cancelCode?: string;
    cancelReason?: string;
    estimatedPickUpTime?: string;
    estimatedDropOffTime?: string;
  };
};

export type PuniyaWebhookPayload = {
  nroPed: number;
  estadoAPI: string;
  timeRetirar: string;
  observaciones?: string;
};

export type MultimediaFile = {
  id: string;
  establishmentId: string;
  details: {
    fileName: string;
    groupName: string;
    uri: string;
    mimeType: string;
    fileSize: number;
    created: Date;
    updated: Date;
  };
};

export type BatchOperationProgress<I, R> = {
  total: number;
  pending: I[];
  completed: { item: I; result: R }[];
  failed: { item: I; error: Error }[];
};

export type ConfirmShippingRequest = {
  establishmentId: string;
  shippingEstimate: ShippingEstimate;
  orderId: string;
};

export type CreateShippingRequest = {
  establishmentId: string;
  providerName: string;
  orderId: string;
};

export type CancelShippingRequest = {
  establishmentId: string;
  shippingOrder: ShippingOrder;
};

export type PuniyaShippingData = {
  accion: 'nuevo';
  idApi: number;
  tokenApi: string;
  valorProducto: number;
  timeRetirar: number;
  rango: 1 | 2 | 3;
  acargo: 0 | 1;
  ciudadOrigen: string;
  calleOrigen: string;
  deptoOrigen?: string;
  alturaOrigen: number;
  telOrigen: number;
  cordsOrigen?: string;
  urlOrigen?: string;
  comentarioOrigen?: string;
  ciudadDestino: string;
  calleDestino: string;
  deptoDestino?: string;
  alturaDestino: number;
  telDestino: number;
  cordsDestino?: string;
  urlDestino?: string;
};
export type CabifyWebhookPayload = {
  id: string;
  external_id?: string;
  event_time: string;
  state: string;
  failure_reason?: string;
  delivery_attempt?: {
    id_proof_of_delivery?: {
      recipient_name?: string;
      recipient_id_number?: string;
    };
    photo_proof_of_delivery?: {
      photo_url?: string;
    };
    comment_proof_of_delivery?: {
      comment?: string;
    };
    fail_reason?: string;
    support_ticket?: string;
    feedback?: string;
  };
  tracking?: {
    eta_to_accept?: number;
    location?: {
      lat: number;
      lon: number;
    };
    tracking_url?: string;
  };
  asset?: {
    reg_plate?: string;
    name: string;
    color: string;
    asset_kind: string;
  };
  driver?: {
    photo_url: string;
    name: string;
    phone: string;
  };
};

export type InstagramIssueConfig = {
  android: boolean;
  ios: boolean;
};

export type CustomerScore = {
  establishmentId: string;
  scores: {
    configurationInitial: number;
    productsUploaded: number;
    categoriesUploaded: number;
    enrichedCatalog: number;
    platformInteraction: number;
    maximizedSystem: number;
    ordersLastMonth: number;
    orders2Months: number;
    ordersLast3Months: number;
    noOrdersDecrease: number;
    total: number;
  };
  lastUpdated: Date;
};

// Interfaces para notificaciones de score
export interface NotificationVariables {
  customerName: string;
  username: string;
  password: string;
  adminUrl: string;
  establishmentUrl: string;
}

export type ScoreWhatsAppVariables = NotificationVariables;
export type ScoreEmailVariables = NotificationVariables;

export interface ScoreNotification {
  establishmentId: string;
  score: number;
  sentDate: Date;
  type: 'initial' | 'reminder' | 'none';
  reminderNumber?: number;
}

// Interfaces adicionales para sistema de notificaciones de score
export interface NotificationResult {
  whatsappSent: number;
  emailSent: number;
  messagesError: number;
}

export interface EstablishmentForNotification {
  id: string;
  slug: string;
  contactInfo?: {
    email?: string;
    phone?: string;
    firstName?: string;
    name?: string;
  };
  customerScore?: CustomerScore;
  subscription?: {
    status: string;
    isTrial?: boolean;
    createdAt?: Date;
  };
  created?: Date;
  account?: {
    username: string;
    password: string;
  };
}

export interface ScoreTemplateConfig {
  templateKey?: string;
  templateId?: string;
  variables?: (
    establishment: EstablishmentForNotification,
  ) => ScoreWhatsAppVariables | ScoreEmailVariables;
}

export interface ScoreTemplates {
  initial: {
    whatsapp: {
      templateKey: string;
      variables?: (establishment: EstablishmentForNotification) => ScoreWhatsAppVariables;
    };
    email: {
      templateId: string;
      variables?: (establishment: EstablishmentForNotification) => ScoreEmailVariables;
    };
  };
  reminders: {
    [key: number]: {
      type: 'whatsapp' | 'email';
      whatsapp?: {
        templateKey: string;
        variables?: (establishment: EstablishmentForNotification) => ScoreWhatsAppVariables;
      };
      email?: {
        templateId: string;
        variables?: (establishment: EstablishmentForNotification) => ScoreEmailVariables;
      };
    };
  };
}

export type AuthMfaCode = {
  uid: string;
  details: {
    created: Date;
    updated: Date;
    verified?: Date;
    code: string;
    userType: 'establishment' | 'invited';
    retries?: number;
    contact?: string;
  };
};

export type AuthMfaSendCodeResponse = {
  uid: string;
  slug: string;
  exception?: 'resend-code-exception' | 'send-new-code-exception';
  dateSent: Date;
  retries: number;
  minutesForResendCode: number;
  minutesForSendNewCode: number;
  maxRetries: number;
  contact?: string;
};

export type RemoteConfigSecurity = {
  enforceMfa: boolean;
};
